import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import authService from 'services/authService'

// Async thunk for user registration
export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Registration failed')
    }
  }
)

// Async thunk for user login
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Login failed')
    }
  }
)

// Async thunk for logout
export const logoutUser = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    await authService.logout()
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Logout failed')
  }
})

// Async thunk for token refresh
export const refreshToken = createAsyncThunk(
  'auth/refresh',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.refreshAccessToken()
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Token refresh failed')
    }
  }
)

const initialState = {
  user: null,
  accessToken: null, // Stored in memory only (lost on page refresh)
  isAuthenticated: false,
  isInitialized: false,
  loading: false,
  error: null,
  registrationSuccess: false,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Clear error
    clearError: state => {
      state.error = null
    },
    // Clear registration success
    clearRegistrationSuccess: state => {
      state.registrationSuccess = false
    },
    // Set credentials from refresh token (on app load)
    setCredentials: (state, action) => {
      const { user, accessToken } = action.payload
      state.user = user
      state.accessToken = accessToken
      state.isAuthenticated = true
      state.isInitialized = true
    },
    // Mark initialization complete even if no credentials found
    initializeComplete: (state) => {
      state.isInitialized = true
    },
    // Clear credentials (logout)
    clearCredentials: state => {
      state.user = null
      state.accessToken = null
      state.isAuthenticated = false
    },
  },
  extraReducers: builder => {
    builder
      // Register
      .addCase(registerUser.pending, state => {
        state.loading = true
        state.error = null
        state.registrationSuccess = false
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false
        state.registrationSuccess = true
        state.error = null
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.registrationSuccess = false
      })
      // Login
      .addCase(loginUser.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.accessToken = action.payload.accessToken
        state.isAuthenticated = true
        state.isInitialized = true
        state.error = null
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.isAuthenticated = false
      })
      // Logout
      .addCase(logoutUser.fulfilled, state => {
        state.user = null
        state.accessToken = null
        state.isAuthenticated = false
        state.loading = false
      })
      // Refresh Token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.accessToken = action.payload.accessToken
        state.user = action.payload.user
      })
  },
})

export const { clearError, clearRegistrationSuccess, setCredentials, clearCredentials, initializeComplete } =
  authSlice.actions

export default authSlice.reducer
